import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Colors } from 'modules/theme';
import Loader from 'components/Loader';
import H5 from 'components/H5';
import {
  FLOW_VOLUME_BAR_RADIUS,
  computeGroupedBarSize,
  formatFlowVolumeTickFromPoint,
  getFlowVolumeChartMargin,
  getFlowVolumePlotWidth,
  getXAxisInterval,
  isDenseFlowVolumeChart,
} from 'utils/flowVolumeChart';
import {
  CHART_TOOLTIP_CURSOR_BAR,
  CHART_TOOLTIP_WRAPPER_STYLE,
  PERMEATE_COLOR,
  RoFlowVolumeTooltip,
} from './chartTooltip';
import { ChartPanel } from './flowVolume.styles';

const CHART_AXIS = '#94a3b8';
const CHART_GRID = '#e3edf5';
const INLET_GRADIENT_ID = 'roInletBarGradient';
const GROUPED_BAR_GAP = 2;

const ScrollViewport = styled.div`
  width: 100%;
  height: 100%;
  overflow-x: ${props => (props.$scrollable ? 'auto' : 'hidden')};
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
`;

const ScrollInner = styled.div`
  height: 100%;
  width: ${props => (props.$scrollable ? `${props.$width}px` : '100%')};
  min-width: ${props => (props.$scrollable ? `${props.$width}px` : '0')};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(248, 251, 255, 0.82);
  z-index: 10;
  border-radius: 12px;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #e8f1f8;
  box-shadow: 0 4px 16px rgba(20, 152, 203, 0.1);
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
`;

const LegendSwatch = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${props => props.color};
`;

export const RoFlowLegend = () => (
  <LegendRow style={{ marginBottom: 0 }}>
    <LegendItem>
      <LegendSwatch color={Colors.lightBlue} />
      Inlet Flow
    </LegendItem>
    <LegendItem>
      <LegendSwatch color={PERMEATE_COLOR} />
      Permeate Flow
    </LegendItem>
  </LegendRow>
);

const RoFlowVolumeChart = ({ id, data, loading }) => {
  const panelRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(600);
  const chartData = loading ? [] : data;
  const pointCount = chartData.length;
  const denseChart = isDenseFlowVolumeChart(pointCount);
  const plotWidth = getFlowVolumePlotWidth(pointCount, chartWidth);
  const barSize = denseChart ? undefined : computeGroupedBarSize(pointCount, chartWidth, 2);
  const xAxisInterval = getXAxisInterval(pointCount, plotWidth, denseChart ? 72 : 64);
  const chartMargin = getFlowVolumeChartMargin(pointCount, denseChart);

  const renderXAxisTick = ({ x, y, payload }) => {
    const point = chartData[payload?.index];
    const text = formatFlowVolumeTickFromPoint(point, pointCount);
    const lines = String(text).split('\n');

    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={12} textAnchor="middle" fill={CHART_AXIS} fontSize={10}>
          {lines.map((line, lineIndex) => (
            <tspan key={lineIndex} x={0} dy={lineIndex === 0 ? 0 : 12}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  useEffect(() => {
    const node = panelRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width) {
        setChartWidth(width);
      }
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <ChartPanel id={id} ref={panelRef}>
      {loading && (
        <LoadingOverlay>
          <LoadingCard>
            <Loader size={30} />
            <H5 color={Colors.gray8} style={{ marginTop: 10, margin: 0 }}>
              Loading chart data...
            </H5>
          </LoadingCard>
        </LoadingOverlay>
      )}
      <ScrollViewport $scrollable={denseChart}>
        <ScrollInner $scrollable={denseChart} $width={plotWidth}>
          <ResponsiveContainer width={denseChart ? plotWidth : '100%'} height="100%" debounce={50}>
            <ComposedChart
              data={chartData}
              barGap={GROUPED_BAR_GAP}
              barCategoryGap={denseChart ? 4 : '10%'}
              margin={chartMargin}
            >
              <defs>
                <linearGradient id={INLET_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3ec5f2" />
                  <stop offset="100%" stopColor={Colors.lightBlue} />
                </linearGradient>
              </defs>
              <XAxis
                type="category"
                dataKey="name"
                axisLine={false}
                tickLine={false}
                stroke={CHART_AXIS}
                interval={xAxisInterval}
                tick={renderXAxisTick}
                tickMargin={8}
                padding={{ left: 8, right: 8 }}
              />
              <YAxis
                yAxisId="flow"
                type="number"
                unit=" gal"
                width={54}
                axisLine={false}
                tickLine={false}
                stroke={CHART_AXIS}
                tick={{ fontSize: 11, fill: CHART_AXIS }}
              />
              <Tooltip
                content={<RoFlowVolumeTooltip />}
                cursor={CHART_TOOLTIP_CURSOR_BAR}
                wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
              />
              <CartesianGrid stroke={CHART_GRID} strokeDasharray="4 6" vertical={false} />
              <Bar
                yAxisId="flow"
                dataKey="galInlet"
                legendType="none"
                fill={`url(#${INLET_GRADIENT_ID})`}
                radius={denseChart ? [2, 2, 0, 0] : FLOW_VOLUME_BAR_RADIUS}
                {...(barSize != null && { barSize })}
                isAnimationActive={!loading}
                animationDuration={600}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`inlet-${index}`} fill={`url(#${INLET_GRADIENT_ID})`} />
                ))}
              </Bar>
              <Bar
                yAxisId="flow"
                dataKey="gal"
                legendType="none"
                fill={PERMEATE_COLOR}
                radius={denseChart ? [2, 2, 0, 0] : FLOW_VOLUME_BAR_RADIUS}
                {...(barSize != null && { barSize })}
                isAnimationActive={!loading}
                animationDuration={600}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`permeate-${index}`} fill={PERMEATE_COLOR} />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </ScrollInner>
      </ScrollViewport>
    </ChartPanel>
  );
};

RoFlowVolumeChart.propTypes = {
  data: PropTypes.array,
  id: PropTypes.string,
  loading: PropTypes.bool,
};

RoFlowVolumeChart.defaultProps = {
  data: [],
  id: 'roFlowVolumeChart',
  loading: false,
};

export default RoFlowVolumeChart;
